import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Image from "./image";
import BusFeatures from "./bus-features";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 357px;
  height: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }

  p,
  span {
    color: black;
  }
`;

const StyledImage = styled(Image)`
  height: 238px;
  width: 100%;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

const Content = styled.div`
  background-color: #dbf7e6;
  width: 100%;
  border-bottom-right-radius: 12px;
  border-bottom-left-radius: 12px;
  padding: 2rem 1rem 0 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 2rem 1rem;
  }
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  width: fit-content;
  margin: 0 0 0.25rem 0;
  color: black;
`;

const SubTitle = styled.p`
  margin: 0 0 1rem 0;
  color: #1d222a;
  width: fit-content;
`;

const PriceList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0.5rem 0 0 0;
  display: grid;
  gap: 12px;
`;

const PriceItem = styled.li`
  display: grid;
  grid-template-areas:
    "line line line"
    "km   km   km";
  row-gap: 4px;
  &:before {
    display: none;
  }
`;

const Line = styled.div`
  grid-area: line;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: baseline;
  gap: 0.6rem;
`;

const FromTo = styled.span`
  font-weight: 600;
`;
const Leader = styled.span`
  border-bottom: 1px solid currentColor;
  opacity: 0.4;
  transform: translateY(-2px);
  align-self: flex-end;
  margin-bottom: 4px;
`;
const Price = styled.span`
  font-weight: 700;
`;
const Km = styled.div`
  grid-area: km;
  font-size: 0.95rem;
  opacity: 0.85;
`;

const FeaturesBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  img {
    filter: none;
  }
`;

const Divider = styled.hr`
  --dot-color: #709d82;
  --dot-size: 2px;
  --gap: 5px;

  border: 0;
  margin: 1rem 0 0 0;
  height: var(--dot-size);
  background: radial-gradient(
      circle,
      var(--dot-color) var(--dot-size),
      transparent var(--dot-size)
    )
    left center / calc(var(--gap) + var(--dot-size)) var(--dot-size) repeat-x;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    margin: 1.5rem 0;
  }
`;

function normalizeRows(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

const nf = new Intl.NumberFormat("nb-NO");

export default function BusPriceExample({
  title,
  subTitle,
  pricerows = [],
  image,
  bus,
}) {
  const rows = useMemo(() => normalizeRows(pricerows), [pricerows]);

  return (
    <Wrapper>
      {image && <StyledImage image={image} />}
      <Content>
        {title && <Title>{title}</Title>}
        {subTitle && <SubTitle>{subTitle}</SubTitle>}

        {rows.length > 0 && (
          <PriceList>
            {rows.map((r, i) => (
              <PriceItem key={i}>
                <Line>
                  <FromTo>
                    {r.from} - {r.to}
                  </FromTo>
                  <Leader />
                  <Price>
                    {r.price ? `${nf.format(Number(r.price))},-` : ""}
                  </Price>
                </Line>
                <Km>{r.km ? `${nf.format(Number(r.km))} km` : ""}</Km>
              </PriceItem>
            ))}
          </PriceList>
        )}

        {bus?.crbChosenBusFeatures?.length > 0 && (
          <FeaturesBlock>
            <Divider />
            <BusFeatures
              bus={bus}
              iconWithLabel
              chipBg="#fff"
              chipBorderColor="#709D82"
            />
          </FeaturesBlock>
        )}
      </Content>
    </Wrapper>
  );
}

BusPriceExample.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  pricerows: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  image: PropTypes.object,
  bus: PropTypes.shape({
    crbChosenBusFeatures: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.bool,
        ]),
      })
    ),
  }),
};
